/**
 * URL Capture Utility
 *
 * iOS Safari's ITP (Intelligent Tracking Prevention) and client-side routing
 * can strip or modify URL parameters (including UTM tracking parameters) after
 * page load. This utility provides a way to capture the original URL early
 * (before any routing or URL modifications) and retrieve it later.
 *
 * Uses window.__SPOTHERO.capturedUrl set by the inline capture code
 * in main.js. This module provides a clean API to access the global value.
 */
/**
 * Gets the captured original URL from page load.
 * Falls back to current window.location.href if not available.
 * @returns {string} The captured URL or current URL
 */
export function getCapturedUrl() {
    if (typeof window === 'undefined') {
        return '';
    }
    try {
        // Return captured URL if available, otherwise current URL
        return window.__SPOTHERO?.capturedUrl ?? window.location.href;
    }
    catch {
        return '';
    }
}
/**
 * Gets the captured referrer value.
 * Falls back to current document.referrer if not available.
 * @returns {string | null} The captured referrer URL or null if not available
 */
export function getCapturedReferrer() {
    if (typeof window === 'undefined') {
        return null;
    }
    try {
        // Return captured referrer if available, otherwise current referrer
        const referrer = window.__SPOTHERO?.capturedReferrer ?? window.document?.referrer;
        // Convert empty string to null
        return referrer || null;
    }
    catch {
        return null;
    }
}
/**
 * Gets page properties from the captured URL to override Segment's automatic capture.
 * This prevents loss of UTM parameters when the URL is modified before tracking fires.
 * @returns {object} Page properties with url, path, and search from captured URL
 */
export function getCapturedPageProperties() {
    if (typeof window === 'undefined') {
        return {
            url: '',
            path: '',
            search: '',
        };
    }
    try {
        const capturedUrl = getCapturedUrl();
        const urlObj = new URL(capturedUrl);
        return {
            url: capturedUrl,
            path: urlObj.pathname,
            search: urlObj.search,
        };
    }
    catch {
        // Fallback to current window.location if parsing fails
        return {
            url: window.location.href,
            path: window.location.pathname,
            search: window.location.search,
        };
    }
}
